var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState } from "react";
import { StepType, IAggregationType, IHistoryBucket } from "@types";
import { findSourceById, getHistoryEnabledFields, getSelectedStepDetailsById, handleStepFieldValueChanges, hasHistoryEnabledForField, registerStep, validateFieldConfiguration, validateSource, validateStep, validateStepNameAndId } from "@utils";
import { FormSection } from "@components/FormSection/FormSection";
import { SourceSelectorInput } from "../SourceSelector/SourceSelectorInput";
import { hasValue, Select } from "uxp/components";
import { ExtendedDatePicker } from "../ExtendedDatePicker/ExtendedDatePicker";
import Icon from '@images/trends.svg';
export function renderStep(context, query, onChange, selectedStepId) {
    const { step, index, previousSteps, selectedSources } = getSelectedStepDetailsById(query, selectedStepId);
    const _step = Object.assign({}, step);
    const $L = (key) => context.uxpContext.$L(key);
    const [errors, setErrors] = useState({});
    useEffect(() => {
        const { isValid, errors } = validateStep(query, step);
        const resolved = Object.fromEntries(Object.entries(errors || {}).map(([k, v]) => [k, v && v.startsWith('lqe.') ? $L(v) : v]));
        setErrors(resolved);
    }, [step]);
    const source = findSourceById(query, _step === null || _step === void 0 ? void 0 : _step.source);
    const historicalSource = findSourceById(query, _step === null || _step === void 0 ? void 0 : _step.historicalDataSourceId);
    function onValueChange(field, value, source) {
        return __awaiter(this, void 0, void 0, function* () {
            const _query = yield handleStepFieldValueChanges(context, query, selectedStepId, field, value, source);
            onChange(_query);
        });
    }
    return React.createElement(React.Fragment, null,
        React.createElement(FormSection, { label: $L('lqe.step.history.source'), error: errors === null || errors === void 0 ? void 0 : errors.source },
            React.createElement(SourceSelectorInput, { source: source, onChange: newSource => {
                    onValueChange('source', newSource, true);
                }, availableSources: [...previousSteps, ...selectedSources], query: query })),
        React.createElement(FormSection, { label: $L('lqe.step.history.field'), error: errors === null || errors === void 0 ? void 0 : errors.field },
            React.createElement(Select, { selected: _step.field || '', onChange: updated => onValueChange('field', updated), options: ((source === null || source === void 0 ? void 0 : source.schema) || []).map(f => ({
                    label: f.name,
                    value: f.name
                })) })),
        React.createElement(FormSection, { label: $L('lqe.step.history.hist-source'), error: errors === null || errors === void 0 ? void 0 : errors.historicalDataSourceId },
            React.createElement(SourceSelectorInput, { source: historicalSource, onChange: newSource => {
                    onValueChange('historicalDataSourceId', newSource, true);
                }, availableSources: [...selectedSources], history: true, query: query })),
        React.createElement(FormSection, { label: $L('lqe.step.history.hist-source-field'), error: errors === null || errors === void 0 ? void 0 : errors.historicalDataSourceField },
            React.createElement(Select, { selected: _step.historicalDataSourceField || '', onChange: updated => onValueChange('historicalDataSourceField', updated), options: (getHistoryEnabledFields(historicalSource) || []).map(f => ({
                    label: f.name,
                    value: f.name
                })) })),
        React.createElement(FormSection, { label: $L('lqe.step.history.aggregation'), error: errors === null || errors === void 0 ? void 0 : errors.aggregation },
            React.createElement(Select, { selected: _step.aggregation || '', onChange: updated => onValueChange('aggregation', updated), options: (Object.entries(IAggregationType) || []).map(f => ({
                    label: f[0],
                    value: f[1]
                })) })),
        React.createElement(FormSection, { label: $L('lqe.step.history.bucket'), error: errors === null || errors === void 0 ? void 0 : errors.bucket },
            React.createElement(Select, { selected: _step.bucket || '', onChange: updated => onValueChange('bucket', updated), options: (Object.entries(IHistoryBucket) || []).map(f => ({
                    label: f[0],
                    value: f[1]
                })) })),
        React.createElement(FormSection, { label: $L('lqe.step.history.start-date'), error: errors === null || errors === void 0 ? void 0 : errors.starts },
            React.createElement(ExtendedDatePicker, { datetime: _step.starts || '0d', onChange: updated => onValueChange('starts', updated) })),
        React.createElement(FormSection, { label: $L('lqe.step.history.end-date'), error: errors === null || errors === void 0 ? void 0 : errors.ends },
            React.createElement(ExtendedDatePicker, { datetime: _step.ends || '0d', onChange: updated => onValueChange('ends', updated) })));
}
function validate(query, step) {
    const errors = validateStepNameAndId(step);
    // validate source
    const source = validateSource(query, step, 'source');
    if (!source.isValid)
        errors.source = source.error;
    const historySource = validateSource(query, step, 'historicalDataSourceId', true, true);
    if (!historySource.isValid)
        errors.historicalDataSourceId = historySource.error;
    const sourceField = validateFieldConfiguration(query, step, [step.field], 'source');
    if (!(sourceField === null || sourceField === void 0 ? void 0 : sourceField.isValid))
        errors.field = sourceField.error;
    const historyField = validateFieldConfiguration(query, step, [step.historicalDataSourceField], 'historicalDataSourceId');
    if (!(historyField === null || historyField === void 0 ? void 0 : historyField.isValid))
        errors.historicalDataSourceField = historyField.error;
    if (!hasHistoryEnabledForField(historySource === null || historySource === void 0 ? void 0 : historySource.source, step.historicalDataSourceField))
        errors.historicalDataSourceField = 'lqe.step.history.error-not-enabled';
    if (!hasValue(step === null || step === void 0 ? void 0 : step.aggregation))
        errors.aggregation = 'lqe.step.history.error-no-aggregation';
    if (!hasValue(step === null || step === void 0 ? void 0 : step.bucket))
        errors.bucket = 'lqe.step.history.error-no-bucket';
    if (!hasValue(step === null || step === void 0 ? void 0 : step.starts))
        errors.starts = 'lqe.step.history.error-no-start';
    if (!hasValue(step === null || step === void 0 ? void 0 : step.ends))
        errors.ends = 'lqe.step.history.error-no-end';
    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
}
registerStep({
    type: StepType.History,
    name: "lqe.step.history.name",
    description: "lqe.step.history.description",
    icon: Icon,
    iconStyles: {
        backgroundSize: '55%'
    },
    render: renderStep,
    validate: validate,
    onSelectStep: (basicProps, previousSource) => {
        return Object.assign(Object.assign({}, basicProps), { source: previousSource, aggregation: 'average', bucket: 'hour', ends: '0d', starts: '-7d', field: '', historicalDataSourceField: '', historicalDataSourceId: '' });
    }
});
